import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useSiteT } from "../../hooks/useSiteT";
import { getClickIdsFromContext, getGaClientId } from "../../lib/ga4";

export default function FinalCTA() {
    const { t } = useSiteT();

    const spendOptions = [
        t("cta.under"),
        "$10,000 - $25,000",
        "$25,000 - $100,000",
        "$100,000 - $500,000",
        "$500,000 - $1,000,000",
        t("cta.over"),
    ];

    const platformOptions = [
        "Meta (Facebook / Instagram)",
        "Google Ads",
        "TikTok Ads",
        "Snapchat Ads",
        "Bing / Microsoft",
        "Taboola / Outbrain",
        t("cta.allPlatforms"),
    ];

    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        monthly_spend: "",
        platform: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.monthly_spend) {
            toast.error(t("cta.required"));
            return;
        }
        setSubmitting(true);
        try {
            const clickIds = getClickIdsFromContext();
            await api.post("/applications", {
                ...form,
                gclid: clickIds.gclid || undefined,
                fbclid: clickIds.fbclid || undefined,
                ga_client_id: getGaClientId() || undefined,
            });
            setSubmitted(true);
            toast.success(t("cta.received"));
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("cta.failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="cta" data-testid="final-cta" className="py-24 md:py-32 relative">
            <div className="container-skala">
                <div className="grid lg:grid-cols-12 gap-12 items-start rounded-2xl border border-white/[0.08] bg-card p-8 md:p-12 lg:p-16 relative overflow-hidden">
                    <div aria-hidden className="absolute top-0 left-0 h-px w-full" style={{ background: "linear-gradient(90deg, transparent, hsl(158 42% 46% / 0.5), transparent)" }} />
                    <div className="lg:col-span-5 space-y-6 relative">
                        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-highlight">{t("cta.eyebrow")}</p>
                        <h2 className="font-display font-semibold text-4xl md:text-5xl tracking-tight leading-[1.05]">
                            {t("cta.h1")}{" "}
                            <span className="serif-accent text-foreground/85">{t("cta.h2")}</span>
                        </h2>
                        <p className="text-muted-foreground text-base md:text-lg max-w-md">{t("cta.sub")}</p>
                        <Link to="/signup" data-testid="final-cta-signup-link">
                            <Button className="h-12 px-6 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-[0_0_30px_rgba(0, 0, 0,0.35)]">
                                {t("cta.signup")} <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>

                        <ul className="space-y-3 pt-4 border-t border-white/5">
                            <li className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground pb-1">{t("cta.whatYouGet")}</li>
                            {["cta.p1", "cta.p2", "cta.p3", "cta.p4", "cta.p5"].map((k) => (
                                <li key={k} className="text-sm text-foreground/85">· {t(k)}</li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-7 relative">
                        {submitted ? (
                            <div data-testid="application-success-state" className="bg-background/50 border border-primary/30 rounded-2xl p-10 text-center space-y-3">
                                <div className="w-12 h-12 rounded-full bg-primary/15 grid place-items-center mx-auto">
                                    <span className="text-primary text-xl">✓</span>
                                </div>
                                <h3 className="font-display font-bold text-2xl">{t("cta.okTitle")}</h3>
                                <p className="text-muted-foreground">{t("cta.okText")}</p>
                            </div>
                        ) : (
                            <form data-testid="application-form" onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="full_name">{t("cta.fullName")}</Label>
                                    <Input id="full_name" data-testid="apply-full-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder={t("cta.namePh")} className="bg-background border-white/10 h-11" required />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="email">{t("cta.email")}</Label>
                                    <Input id="email" data-testid="apply-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder={t("cta.emailPh")} className="bg-background border-white/10 h-11" required />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="phone">{t("cta.phone")}</Label>
                                    <Input id="phone" data-testid="apply-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1 555 000 0000" className="bg-background border-white/10 h-11" />
                                </div>

                                <div className="space-y-1.5">
                                    <Label>{t("cta.spend")}</Label>
                                    <Select value={form.monthly_spend} onValueChange={(v) => update("monthly_spend", v)}>
                                        <SelectTrigger data-testid="apply-monthly-spend" className="bg-background border-white/10 h-11">
                                            <SelectValue placeholder={t("cta.spendPh")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {spendOptions.map((s) => (
                                                <SelectItem key={s} value={s}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-1.5">
                                    <Label>{t("cta.platform")}</Label>
                                    <Select value={form.platform} onValueChange={(v) => update("platform", v)}>
                                        <SelectTrigger data-testid="apply-platform" className="bg-background border-white/10 h-11">
                                            <SelectValue placeholder={t("cta.platformPh")} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {platformOptions.map((s) => (
                                                <SelectItem key={s} value={s}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-1.5 sm:col-span-2">
                                    <Label htmlFor="message">{t("cta.message")}</Label>
                                    <Textarea id="message" data-testid="apply-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder={t("cta.messagePh")} rows={4} className="bg-background border-white/10" />
                                </div>

                                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                                    <Button type="submit" disabled={submitting} data-testid="apply-submit-button" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 px-8 w-full sm:w-auto">
                                        {submitting ? t("cta.submitting") : t("cta.submit")}
                                    </Button>
                                    <p className="text-xs text-muted-foreground font-mono">{t("cta.reply")}</p>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
